import React from 'react';
import Feather from '@expo/vector-icons/Feather';
import { View, TouchableOpacity, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { loginWithGoogle, continueAsGuest } from '../../redux/authSlice';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';

interface SocialAuthButtonsProps {
  onContinueAsGuest?: () => void;
}

export const SocialAuthButtons: React.FC<SocialAuthButtonsProps> = ({ onContinueAsGuest }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector((state: RootState) => state.auth);

  const handleGoogleSignIn = async () => {
    console.log('🔐 User clicked Google Sign-In button');
    try {
      await dispatch(loginWithGoogle()).unwrap();
      // If successful, redirect will happen automatically in AuthScreen
      console.log('✅ Google Sign-In initiated successfully');
    } catch (err: any) {
      console.error('❌ Google sign-in error:', err);
      // Error will be displayed via the error state from Redux
      // The error is already set in the auth slice, so it will be shown below
    }
  };

  const handleContinueAsGuest = () => {
    dispatch(continueAsGuest());
    if (onContinueAsGuest) {
      onContinueAsGuest();
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('auth.signInToSync')}</Text>

      {/* Google Sign-In Button */}
      <TouchableOpacity
        style={[styles.button, styles.googleButton]}
        onPress={handleGoogleSignIn}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <>
            <Feather name="lock" size={18} color="#fff" style={styles.buttonIcon} />
            <Text style={styles.buttonText}>{t('auth.continueWithGoogle')}</Text>
          </>
        )}
      </TouchableOpacity>

      <View style={styles.divider}>
        <View style={styles.dividerLine} />
        <Text style={styles.dividerText}>{t('auth.or')}</Text>
        <View style={styles.dividerLine} />
      </View>

      {/* Continue as Guest */}
      <TouchableOpacity
        style={[styles.button, styles.guestButton]}
        onPress={handleContinueAsGuest}
        disabled={loading}
      >
        <Text style={styles.guestButtonText}>{t('auth.continueAsGuest')}</Text>
        <Text style={styles.guestSubtext}>{t('auth.localStorageOnly')}</Text>
      </TouchableOpacity>

      {!!error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <Text style={styles.infoText}>
        {t('auth.dataAgreement')}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: 24,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginBottom: 12,
  },
  googleButton: {
    backgroundColor: '#4285F4',
  },
  buttonIcon: {
    marginRight: 12,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  guestButton: {
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    flexDirection: 'column',
    paddingVertical: 16,
  },
  guestButtonText: {
    color: Paprika.textPrimary,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  guestSubtext: {
    color: '#52443c',
    fontSize: 12,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#e0e0e0',
  },
  dividerText: {
    marginHorizontal: 12,
    color: '#52443c',
    fontSize: 14,
  },
  errorContainer: {
    backgroundColor: '#FFEBEE',
    padding: 12,
    borderRadius: 6,
    marginTop: 12,
  },
  errorText: {
    color: '#C62828',
    fontSize: 14,
    textAlign: 'center',
  },
  infoText: {
    marginTop: 20,
    fontSize: 12,
    color: '#85746b',
    textAlign: 'center',
    lineHeight: 18,
  },
});
